import { useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import styled from 'styled-components';
import { COLORS, DATA_TYPE_ERROR_MESSAGE, device } from '../../../../constants';
import { SliderRightArrow } from './SliderRightArrow';
import { SliderLeftArrow } from './SliderLeftArrow';
import React from 'react';
import { Box, ProgressBar } from '../../../../components';

type PortfolioSliderProps<T> = {
  data: T[];
  renderItem: (item: T) => ReactNode;
};

export const PortfolioSlider = <T,>({
  data,
  renderItem,
}: PortfolioSliderProps<T>) => {
  const [isOverflow, setIsOverflow] = useState<boolean>(false);
  const [isAtStart, setIsAtStart] = useState<boolean>(true);
  const [isAtEnd, setIsAtEnd] = useState<boolean>(false);
  const [visibleIndex, setVisibleIndex] = useState<number>(0);
  const swiperRef = useRef<HTMLDivElement | null>(null);
  const isManualScroll = useRef<boolean>(false);
  const handleScrollRef = useRef<(() => void) | null>(null);

  const updateVisibleIndex = useCallback(() => {
    if (swiperRef.current) {
      if (!isManualScroll.current) {
        const scrollLeft = swiperRef.current.scrollLeft;
        const list = swiperRef.current.children[0];
        const zoneWidth =
          (list.clientWidth - swiperRef.current.clientWidth) /
          (data.length - 1);
        const index = Math.round(scrollLeft / zoneWidth);
        setVisibleIndex(index);
      }
    }
  }, [data.length]);

  const handleManualScroll = useCallback(
    (index: number) => {
      if (swiperRef.current) {
        isManualScroll.current = true;
        const list = swiperRef.current.children[0];
        const zoneWidth =
          (list.clientWidth - swiperRef.current.clientWidth) /
          (data.length - 1);
        const targetScrollLeft = zoneWidth * index;
        swiperRef.current.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth',
        });
        const waitForScrollEnd = () => {
          if (!swiperRef.current) return;
          const currentScrollLeft = swiperRef.current.scrollLeft;
          if (Math.abs(currentScrollLeft - targetScrollLeft) < 10) {
            isManualScroll.current = false;
          } else {
            requestAnimationFrame(waitForScrollEnd);
          }
        };
        requestAnimationFrame(waitForScrollEnd);
      }
    },
    [data.length]
  );
  const barElementClick = useCallback(
    (index: number) => {
      if (swiperRef.current && !isManualScroll.current) {
        setVisibleIndex(index);
        handleManualScroll(index);
      }
    },
    [handleManualScroll]
  );
  const checkForOverflow = useCallback(() => {
    if (swiperRef.current) {
      setIsOverflow(
        swiperRef.current.clientWidth < swiperRef.current.scrollWidth
      );
    }
  }, [swiperRef.current]);

  const updateArrowStates = useCallback(() => {
    if (swiperRef.current) {
      setIsAtStart(visibleIndex === 0);
      setIsAtEnd(visibleIndex === data.length - 1);
    }
  }, [visibleIndex, data.length]);

  const scrollLeft = useCallback(() => {
    if (swiperRef.current && !isManualScroll.current) {
      handleManualScroll(Math.max(visibleIndex - 1, 0));
      setVisibleIndex(prevState => Math.max(prevState - 1, 0));
    }
  }, [visibleIndex, handleManualScroll]);

  const scrollRight = useCallback(() => {
    if (swiperRef.current && !isManualScroll.current) {
      handleManualScroll(Math.min(visibleIndex + 1, data.length - 1));
      setVisibleIndex(prevState => Math.min(prevState + 1, data.length - 1));
    }
  }, [visibleIndex, data.length, handleManualScroll]);

  useEffect(() => {
    checkForOverflow();
    window.addEventListener('resize', checkForOverflow);
    return () => window.removeEventListener('resize', checkForOverflow);
  }, [data, checkForOverflow]);

  useEffect(() => {
    handleScrollRef.current = () => {
      if (!isManualScroll.current) {
        updateVisibleIndex();
      }
      updateArrowStates();
    };
  }, [visibleIndex, updateArrowStates, updateVisibleIndex]);

  useEffect(() => {
    updateArrowStates();
    const swiper = swiperRef.current;
    const handleScroll = () => {
      handleScrollRef.current?.();
    };
    swiper?.addEventListener('scroll', handleScroll);
    return () => {
      swiper?.removeEventListener('scroll', handleScroll);
    };
  }, [data, updateArrowStates]);

  return (
    <Wrapper>
      {data.length > 0 ? (
        <ProjectListContainer $isOverflow={isOverflow} ref={swiperRef}>
          <ProjectList>
            {data.map((item, index) => (
              <React.Fragment key={index}>{renderItem(item)}</React.Fragment>
            ))}
          </ProjectList>
          {isOverflow && (
            <>
              <ArrowLeft onClick={scrollLeft} $disabled={isAtStart}>
                <SliderLeftArrow color={COLORS.WHITE} />
              </ArrowLeft>
              <ArrowRight onClick={scrollRight} $disabled={isAtEnd}>
                <SliderRightArrow color={COLORS.WHITE} />
              </ArrowRight>
            </>
          )}
        </ProjectListContainer>
      ) : (
        <ErrorText>{DATA_TYPE_ERROR_MESSAGE}</ErrorText>
      )}
      {isOverflow && (
        <OverflowContainer>
          <Box>
            <ProgressBar
              data={data}
              visibleIndex={visibleIndex}
              barElementClick={barElementClick}
            />
          </Box>
        </OverflowContainer>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin-top: 80px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media screen and ${device.lg} {
    margin-top: 30px;
  }
  @media screen and ${device.xs} {
    flex-direction: column;
    gap: 10px;
  }
`;
const ArrowLeft = styled.div<{ $disabled?: boolean }>`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 35px;
  height: 70px;
  background-color: rgba(0, 0, 0, 0.35);
  border-radius: 0 35px 35px 0;
  display: none;
  visibility: ${props => (props.$disabled ? 'hidden' : 'visible')};
  pointer-events: ${props => (props.$disabled ? 'none' : 'auto')};
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  transition: transform 0.2s;

  svg {
    width: 25px;
    height: 25px;
    color: ${props => (props.$disabled ? COLORS.WHITE : '#2E2E2E')};
  }

  &:hover {
    transform: ${props =>
      'translateY(-50%)' +
      (props.$disabled ? ' transform: translateY(-50%);' : 'scale(1.1)')};
  }
  @media screen and ${device.md} {
    display: flex;
  }
`;

const ArrowRight = styled.div<{ $disabled?: boolean }>`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 35px;
  height: 70px;
  background-color: rgba(0, 0, 0, 0.35);
  border-radius: 35px 0 0 35px;
  display: none;
  align-items: center;
  visibility: ${props => (props.$disabled ? 'hidden' : 'visible')};
  pointer-events: ${props => (props.$disabled ? 'none' : 'auto')};
  justify-content: flex-end;
  cursor: pointer;
  transition: transform 0.2s;

  svg {
    width: 25px;
    height: 25px;
  }

  &:hover {
    transform: ${props =>
      'translateY(-50%)' +
      (props.$disabled ? ' transform: translateY(-50%);' : 'scale(1.1)')};
  }
  @media screen and ${device.md} {
    display: flex;
  }
`;
const ProjectListContainer = styled.div<{ $isOverflow: boolean }>`
  overflow: scroll;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-snap-type: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;
  user-select: none;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    display: none;
  }

  display: ${props => (!props.$isOverflow ? 'flex' : 'block')};
  justify-content: center;
  @media screen and ${device.xs} {
    width: 100%;
  }

  &:hover ${ArrowLeft}, &:hover ${ArrowRight} {
    display: flex;
  }
`;

const ProjectList = styled.div`
  display: flex;
  gap: 20px;
  padding-bottom: 10px;
  height: 100%;
  width: max-content;
  transition: transform 0.2s ease-in-out;
`;
const OverflowContainer = styled.div`
  * {
    user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    -webkit-user-select: none;
  }

  > div {
    display: flex;
    align-items: center;
    max-width: 100%;
    padding-top: 40px;
    @media screen and ${device.sm} {
      flex-direction: column;
      gap: 5px;
      padding-top: 20px;
    }
    justify-content: space-between;
  }
`;
const ErrorText = styled.p`
  color: red;
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  margin-top: 20px;
`;
